import React, { useEffect, useRef, useState } from 'react';
import { Card } from '../ui/card';
import { formatCurrency } from '../../data/mockData';
import { PieChart, BarChart3, TrendingUp, Activity } from 'lucide-react';

const ChartsSection = ({ departmentStats, projects }) => {
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const totalAmount = departmentStats.reduce((sum, dept) => sum + (dept.total_sanctioned_amount || dept.totalAmount || 0), 0);
  
  // Calculate project status distribution
  const statusDistribution = {
    'Completed': projects.filter(p => p.status === 'Completed').length,
    'In Progress': projects.filter(p => p.status === 'In Progress').length,
    'Delayed': projects.filter(p => p.status === 'Delayed').length,
    'Not Started': projects.filter(p => p.status === 'Not Started').length
  };

  const statusColors = {
    'Completed': 'from-green-400 to-green-500',
    'In Progress': 'from-blue-400 to-blue-500',
    'Delayed': 'from-red-400 to-red-500',
    'Not Started': 'from-gray-400 to-gray-500'
  };

  const departmentColors = [
    '#3B82F6', // Blue - PWD
    '#06B6D4', // Cyan - Water Supply  
    '#F97316', // Orange - Electric
    '#10B981', // Emerald - Health
    '#8B5CF6', // Purple - IT
    '#059669'  // Green - Garden
  ];

  const handleMouseMove = (event, index) => {
    setHoveredSegment(index);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredSegment(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      {/* Department-wise Budget Allocation - Compact Pie Chart */}
      <Card className="p-6 bg-gradient-to-br from-white to-blue-50 border-0 shadow-lg">
        <div className="flex items-center space-x-3 mb-8">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
            <PieChart className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">Budget Allocation</h3>
            <p className="text-sm text-slate-600">Total: {formatCurrency(totalAmount)}</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between gap-8">
          {/* Larger Pie Chart */}
          <div className="relative w-64 h-64 flex-shrink-0">
            <svg 
              className="w-64 h-64 transform -rotate-90 drop-shadow-lg" 
              viewBox="0 0 200 200"
            >
              {departmentStats.map((dept, index) => {
                const percentage = ((dept.total_sanctioned_amount || dept.totalAmount || 0) / totalAmount) * 100;
                const angle = (percentage / 100) * 360;
                const radius = 85; // Increased from 75
                const centerX = 100;
                const centerY = 100;
                
                // Calculate start angle (sum of previous segments)
                const startAngle = departmentStats.slice(0, index).reduce((sum, d) => {
                  return sum + (((d.total_sanctioned_amount || d.totalAmount || 0) / totalAmount) * 360);
                }, 0);
                
                const endAngle = startAngle + angle;
                
                // Convert to radians
                const startAngleRad = (startAngle * Math.PI) / 180;
                const endAngleRad = (endAngle * Math.PI) / 180;
                
                // Calculate arc points
                const x1 = centerX + radius * Math.cos(startAngleRad);
                const y1 = centerY + radius * Math.sin(startAngleRad);
                const x2 = centerX + radius * Math.cos(endAngleRad);
                const y2 = centerY + radius * Math.sin(endAngleRad);
                
                const largeArc = angle > 180 ? 1 : 0;
                
                // For hover effect, slightly increase radius
                const hoverRadius = hoveredSegment === index ? radius + 10 : radius; // Increased hover effect
                const hoverX1 = centerX + hoverRadius * Math.cos(startAngleRad);
                const hoverY1 = centerY + hoverRadius * Math.sin(startAngleRad);
                const hoverX2 = centerX + hoverRadius * Math.cos(endAngleRad);
                const hoverY2 = centerY + hoverRadius * Math.sin(endAngleRad);
                
                const pathData = [
                  `M ${centerX} ${centerY}`,
                  `L ${hoverX1} ${hoverY1}`,
                  `A ${hoverRadius} ${hoverRadius} 0 ${largeArc} 1 ${hoverX2} ${hoverY2}`,
                  'Z'
                ].join(' ');
                
                return (
                  <path
                    key={dept.department}
                    d={pathData}
                    fill={departmentColors[index]}
                    stroke="white"
                    strokeWidth="3"
                    className="cursor-pointer transition-all duration-300 ease-out hover:brightness-110"
                    style={{
                      filter: hoveredSegment === index ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' : 'none',
                      transform: hoveredSegment === index ? 'scale(1.02)' : 'scale(1)',
                      transformOrigin: 'center'
                    }}
                    onMouseMove={(e) => handleMouseMove(e, index)}
                    onMouseLeave={handleMouseLeave}
                  />
                );
              })}
            </svg>
            
            {/* Center circle with total info */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-white rounded-full w-24 h-24 flex items-center justify-center shadow-lg border-2 border-slate-200">
                <div className="text-center">
                  <div className="text-base font-bold text-slate-800">{departmentStats.length}</div>
                  <div className="text-xs text-slate-600">Depts</div>
                </div>
              </div>
            </div>
          </div>

          {/* Legend - More compact and organized */}
          <div className="flex-1 max-w-xs space-y-3">
            {departmentStats.map((dept, index) => {
              const percentage = ((dept.total_sanctioned_amount || dept.totalAmount || 0) / totalAmount) * 100;
              return (
                <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-3 h-3 rounded-full shadow-sm flex-shrink-0" 
                      style={{ backgroundColor: departmentColors[index] }}
                    ></div>
                    <span className="text-sm font-semibold text-slate-700">{dept.department}</span>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-sm font-bold text-slate-800">{formatCurrency(dept.total_sanctioned_amount || dept.totalAmount || 0)}</div>
                    <div className="text-xs text-slate-500">{percentage.toFixed(1)}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tooltip */}
        {hoveredSegment !== null && (
          <div
            className="fixed z-50 bg-slate-800 text-white px-3 py-2 rounded-lg shadow-xl pointer-events-none transition-opacity duration-200"
            style={{
              left: tooltipPosition.x + 10,
              top: tooltipPosition.y - 10,
              transform: 'translate(0, -100%)'
            }}
          >
            <div className="text-sm font-semibold">{departmentStats[hoveredSegment].department}</div>
            <div className="text-xs">
              {formatCurrency(departmentStats[hoveredSegment].total_sanctioned_amount || departmentStats[hoveredSegment].totalAmount || 0)} 
              <span className="ml-1 opacity-75">
                ({(((departmentStats[hoveredSegment].total_sanctioned_amount || departmentStats[hoveredSegment].totalAmount || 0) / totalAmount) * 100).toFixed(1)}%)
              </span>
            </div>
          </div>
        )}
      </Card>

      {/* Project Stage Distribution - Bar Chart */}
      <Card className="p-6 bg-gradient-to-br from-white to-green-50 border-0 shadow-lg">
        <div className="flex items-center space-x-3 mb-8">
          <div className="p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl">
            <BarChart3 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">Project Status Distribution</h3>
            <p className="text-sm text-slate-600">Current project status overview</p>
          </div>
        </div>

        {/* Bar Chart */}
        <div className="space-y-5">
          {Object.entries(statusDistribution).map(([status, count]) => {
            const maxCount = Math.max(...Object.values(statusDistribution));
            const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
            
            return (
              <div key={status} className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-slate-700">{status}</span>
                  <div className="text-right">
                    <span className="text-lg font-bold text-slate-800">{count}</span>
                    <span className="text-xs text-slate-500 ml-1">projects</span>
                  </div>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-4">
                  <div 
                    className={`bg-gradient-to-r ${statusColors[status]} h-4 rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-2`}
                    style={{ width: `${percentage}%` }}
                  >
                    {count > 0 && (
                      <span className="text-xs font-semibold text-white">{count}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 pt-6 border-t border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-green-600" />
              <span className="text-sm text-slate-600">Project Pipeline</span>
            </div>
            <span className="text-lg font-bold text-green-600">
              {projects.length} Total
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ChartsSection;